import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {
  CONNECT_GUIDED_CURRENT_ITEM_BINDINGS,
  CONNECT_GUIDED_REQUEST_BINDINGS,
  ConnectGuidedBindingToken,
  ConnectGuidedPriorStepBinding,
  ConnectNodeFieldShape,
  ConnectNodeJsonScalar,
  ConnectNodeJsonType,
  ConnectNodeOperationEffect,
  ConnectNodePresentationField,
  ConnectNodePresentationOperation,
  ConnectWorkflowNodePaletteItem,
  isConnectGuidedBindingToken,
  isConnectGuidedCurrentItemBindingToken,
  isConnectNodeOutputCompatible,
  readConnectNodeOperationInput,
} from '../../../services/connect/connect-nodes.service';

export type ConnectGuidedModuleDraft = {
  paletteId: string;
  workflowType: string;
  providerId?: string;
  action: string;
  ref: string;
  effect: ConnectNodeOperationEffect;
  input: Record<string, unknown>;
};

type ShapeAddress = readonly (string | number)[];
type MapEntry = {key: string; value: unknown};
type BindingChoice = 'fixed' | ConnectGuidedBindingToken;
const RESERVED_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

@Component({
  selector: 'app-connect-guided-module-editor',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './connect-guided-module-editor.component.html',
  styleUrl: './connect-guided-module-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectGuidedModuleEditorComponent {
  private static nextInstanceId = 0;

  readonly node = input.required<ConnectWorkflowNodePaletteItem>();
  readonly initialDraft = input<ConnectGuidedModuleDraft | null>(null);
  readonly allowCurrentItemBindings = input(false);
  readonly priorStepBindings = input<readonly ConnectGuidedPriorStepBinding[]>([]);
  readonly draftReady = output<ConnectGuidedModuleDraft | null>();

  readonly selectedAction = signal('');
  private readonly fieldValues = signal<Record<string, unknown>>({});

  readonly operations = computed(() => this.node().presentation?.operations ?? []);
  readonly selectedOperation = computed(() => this.operations().find(
    operation => operation.action === this.selectedAction(),
  ) ?? null);
  readonly visibleFields = computed<{path: string; field: ConnectNodePresentationField}[]>(() => {
    const presentation = this.node().presentation;
    const fields: {path: string; field: ConnectNodePresentationField}[] = [];
    for (const path of this.selectedOperation()?.fields ?? []) {
      const field = presentation?.fields[path];
      if (field) fields.push({path, field});
    }
    return fields;
  });
  readonly draft = computed<ConnectGuidedModuleDraft | null>(() => {
    const node = this.node();
    const presentation = node.presentation;
    const operation = this.selectedOperation();
    if (!presentation || !operation) return null;
    const raw = this.buildRawInput(operation);
    if (!this.allowCurrentItemBindings() && this.containsCurrentItemBinding(raw)) return null;
    const normalized = readConnectNodeOperationInput(
      presentation,
      operation.action,
      raw,
      {
        allowGuidedBindings: true,
        validatePriorStepBinding: (binding, targetShape) => this.priorStepBindings().some(option =>
          option.value === binding && isConnectNodeOutputCompatible(option.shape, targetShape)),
      },
    );
    if (!normalized) return null;
    return {
      paletteId: node.paletteId,
      workflowType: node.workflowType,
      ...(node.providerId ? {providerId: node.providerId} : {}),
      action: operation.action,
      ref: `${node.paletteId}.${operation.action}`,
      effect: operation.effect,
      input: normalized,
    };
  });

  private lastSessionKey = '';
  private readonly instanceId = `connect-guided-module-${ConnectGuidedModuleEditorComponent.nextInstanceId++}`;

  constructor() {
    effect(() => {
      const node = this.node();
      const initial = this.initialDraft();
      const sessionKey = [node.paletteId, initial?.paletteId, initial?.action, initial?.ref].join('|');
      if (sessionKey === this.lastSessionKey) return;
      this.lastSessionKey = sessionKey;
      untracked(() => this.openSession(node, initial));
    });
    effect(() => {
      this.draftReady.emit(this.draft());
    });
  }

  selectOperation(action: string): void {
    const operation = this.operations().find(candidate => candidate.action === action);
    if (!operation) return;
    this.selectedAction.set(action);
    this.fieldValues.set(this.requiredFixedValues(this.node(), operation));
  }

  effectLabel(effect: ConnectNodeOperationEffect): string {
    if (effect === 'read') return 'Reads information';
    if (effect === 'write') return 'Changes information';
    return 'Removes information';
  }

  effectIcon(effect: ConnectNodeOperationEffect): string {
    if (effect === 'read') return 'visibility';
    if (effect === 'write') return 'edit';
    return 'delete_outline';
  }

  isFieldRequired(path: string): boolean {
    return this.selectedOperation()?.requires_all?.includes(path) ?? false;
  }

  requirementHint(): string {
    const operation = this.selectedOperation();
    if (!operation) return '';
    const labels = (paths: string[]) => paths
      .map(path => this.node().presentation?.fields[path]?.label ?? path)
      .join(' or ');
    const all = (operation.requires_all ?? [])
      .map(path => this.node().presentation?.fields[path]?.label ?? path);
    const any = (operation.requires_any ?? []).map(labels);
    return [...all, ...any].join(', ');
  }

  valueAt(fieldPath: string, address: ShapeAddress = []): unknown {
    let cursor = this.fieldValues()[fieldPath];
    for (const segment of address) {
      if (typeof segment === 'number') {
        if (!Array.isArray(cursor)) return undefined;
        cursor = cursor[segment];
      } else {
        if (!this.isRecord(cursor)) return undefined;
        cursor = cursor[segment];
      }
    }
    return cursor;
  }

  stringValue(fieldPath: string, address: ShapeAddress): string {
    const value = this.valueAt(fieldPath, address);
    return typeof value === 'string' ? value : '';
  }

  numberValue(fieldPath: string, address: ShapeAddress): number | '' {
    const value = this.valueAt(fieldPath, address);
    return typeof value === 'number' && Number.isFinite(value) ? value : '';
  }

  booleanValue(fieldPath: string, address: ShapeAddress): boolean {
    return this.valueAt(fieldPath, address) === true;
  }

  setTextValue(fieldPath: string, address: ShapeAddress, event: Event): void {
    this.setValue(fieldPath, address, (event.target as HTMLInputElement).value);
  }

  setNumberValue(fieldPath: string, address: ShapeAddress, event: Event, integer: boolean): void {
    const text = (event.target as HTMLInputElement).value;
    if (!text.trim()) {
      this.setValue(fieldPath, address, undefined);
      return;
    }
    const value = Number(text);
    this.setValue(fieldPath, address, integer ? Math.trunc(value) : value);
  }

  setBooleanValue(fieldPath: string, address: ShapeAddress, checked: boolean): void {
    this.setValue(fieldPath, address, checked);
  }

  setScalarValue(fieldPath: string, address: ShapeAddress, value: ConnectNodeJsonScalar): void {
    this.setValue(fieldPath, address, value);
  }

  bindingOptions(
    shape: ConnectNodeFieldShape = {type: 'string'},
  ): readonly {value: ConnectGuidedBindingToken; label: string}[] {
    const fixed = this.allowCurrentItemBindings()
      ? [...CONNECT_GUIDED_REQUEST_BINDINGS, ...CONNECT_GUIDED_CURRENT_ITEM_BINDINGS]
      : [...CONNECT_GUIDED_REQUEST_BINDINGS];
    return [
      ...fixed,
      ...this.priorStepBindings()
        .filter(binding => isConnectNodeOutputCompatible(binding.shape, shape))
        .map(binding => ({value: binding.value, label: binding.label})),
    ];
  }

  bindingChoice(fieldPath: string, address: ShapeAddress): BindingChoice {
    const value = this.valueAt(fieldPath, address);
    return isConnectGuidedBindingToken(value) ? value : 'fixed';
  }

  bindingChoiceAvailable(fieldPath: string, address: ShapeAddress, shape: ConnectNodeFieldShape): boolean {
    const choice = this.bindingChoice(fieldPath, address);
    return choice === 'fixed' || this.bindingOptions(shape).some(option => option.value === choice);
  }

  bindingUnavailableMessage(fieldPath: string, address: ShapeAddress): string {
    return isConnectGuidedCurrentItemBindingToken(this.valueAt(fieldPath, address))
      ? 'Current item information is available only inside “For every item”.'
      : 'That earlier value is no longer available here.';
  }

  selectBinding(
    fieldPath: string,
    address: ShapeAddress,
    shape: ConnectNodeFieldShape,
    choice: BindingChoice,
  ): void {
    if (choice !== 'fixed' && !this.bindingOptions(shape).some(option => option.value === choice)) return;
    this.setValue(fieldPath, address, choice === 'fixed' ? this.defaultValue(shape) : choice);
  }

  isBindingCapableShape(shape: ConnectNodeFieldShape): boolean {
    return !shape.oneOf && !this.isFixedSingleValueEnum(shape) && this.shapeTypes(shape).includes('string')
      && !this.shapeTypes(shape).some(type => type === 'array' || type === 'object');
  }

  isFixedSingleValueEnum(shape: ConnectNodeFieldShape): boolean {
    return !shape.oneOf && shape.enum?.length === 1 && this.selectedOperation()?.effect !== 'read';
  }

  shapeKind(shape: ConnectNodeFieldShape): ConnectNodeJsonType | 'oneOf' | 'enum' {
    if (shape.oneOf) return 'oneOf';
    if (shape.enum) return 'enum';
    return this.shapeTypes(shape)[0] ?? 'string';
  }

  hasTypeChoice(shape: ConnectNodeFieldShape): boolean {
    return !shape.oneOf && !shape.enum && this.shapeTypes(shape).length > 1;
  }

  typeOptions(shape: ConnectNodeFieldShape): ConnectNodeJsonType[] {
    return this.shapeTypes(shape);
  }

  selectedType(fieldPath: string, address: ShapeAddress, shape: ConnectNodeFieldShape): ConnectNodeJsonType {
    const value = this.valueAt(fieldPath, address);
    return this.shapeTypes(shape).find(type => this.valueMatchesType(value, type)) ?? this.shapeTypes(shape)[0];
  }

  selectType(
    fieldPath: string,
    address: ShapeAddress,
    shape: ConnectNodeFieldShape,
    type: ConnectNodeJsonType,
  ): void {
    if (!this.shapeTypes(shape).includes(type)) return;
    this.setValue(fieldPath, address, this.defaultValue({type}));
  }

  shapeForType(shape: ConnectNodeFieldShape, type: ConnectNodeJsonType): ConnectNodeFieldShape {
    return {...shape, type};
  }

  selectedVariant(fieldPath: string, address: ShapeAddress, shape: ConnectNodeFieldShape): number {
    const value = this.valueAt(fieldPath, address);
    const index = (shape.oneOf ?? []).findIndex(candidate => this.valueMatchesShapeKind(value, candidate));
    return index >= 0 ? index : 0;
  }

  selectVariant(fieldPath: string, address: ShapeAddress, shape: ConnectNodeFieldShape, index: number): void {
    const variant = shape.oneOf?.[index];
    if (variant) this.setValue(fieldPath, address, this.defaultValue(variant));
  }

  variantShape(shape: ConnectNodeFieldShape, index: number): ConnectNodeFieldShape {
    return shape.oneOf?.[index] ?? {type: 'string'};
  }

  optionLabel(shape: ConnectNodeFieldShape, index: number): string {
    const type = this.shapeTypes(shape.oneOf?.[index] ?? {})[0];
    if (type === 'array') return 'A list of values';
    if (type === 'object') return 'Named values';
    if (type === 'boolean') return 'Yes or no';
    if (type === 'number' || type === 'integer') return 'A number';
    return 'Text';
  }

  enumLabel(value: ConnectNodeJsonScalar): string {
    if (value === null) return 'Empty';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  arrayValues(fieldPath: string, address: ShapeAddress): unknown[] {
    const value = this.valueAt(fieldPath, address);
    return Array.isArray(value) ? value : [];
  }

  addArrayItem(fieldPath: string, address: ShapeAddress, shape: ConnectNodeFieldShape): void {
    const current = this.arrayValues(fieldPath, address);
    this.setValue(fieldPath, address, [...current, this.defaultValue(shape.items ?? {type: 'string'})]);
  }

  removeArrayItem(fieldPath: string, address: ShapeAddress, index: number): void {
    this.setValue(fieldPath, address, this.arrayValues(fieldPath, address).filter((_, itemIndex) => itemIndex !== index));
  }

  objectProperties(shape: ConnectNodeFieldShape): {key: string; shape: ConnectNodeFieldShape}[] {
    return Object.entries(shape.properties ?? {}).map(([key, childShape]) => ({key, shape: childShape}));
  }

  isObjectPropertyRequired(shape: ConnectNodeFieldShape, key: string): boolean {
    return shape.required?.includes(key) ?? false;
  }

  mapEntries(fieldPath: string, address: ShapeAddress): MapEntry[] {
    const value = this.valueAt(fieldPath, address);
    if (!this.isRecord(value)) return [];
    return Object.entries(value).map(([key, entryValue]) => ({key, value: entryValue}));
  }

  addMapEntry(fieldPath: string, address: ShapeAddress, shape: ConnectNodeFieldShape): void {
    const value = this.recordValue(fieldPath, address);
    let index = Object.keys(value).length + 1;
    while (Object.hasOwn(value, `field${index}`)) index += 1;
    const additionalShape = typeof shape.additionalProperties === 'object'
      ? shape.additionalProperties
      : {type: 'string'} as ConnectNodeFieldShape;
    value[`field${index}`] = this.defaultValue(additionalShape);
    this.setValue(fieldPath, address, value);
  }

  renameMapEntry(fieldPath: string, address: ShapeAddress, oldKey: string, event: Event): void {
    const newKey = (event.target as HTMLInputElement).value.trim();
    if (!newKey || !this.isSafeObjectKey(newKey) || newKey === oldKey) return;
    const value = this.recordValue(fieldPath, address);
    if (Object.hasOwn(value, newKey)) return;
    const entries = Object.entries(value).map(([key, child]) => key === oldKey ? [newKey, child] : [key, child]);
    this.setValue(fieldPath, address, Object.fromEntries(entries));
  }

  removeMapEntry(fieldPath: string, address: ShapeAddress, key: string): void {
    const value = this.recordValue(fieldPath, address);
    delete value[key];
    this.setValue(fieldPath, address, value);
  }

  childAddress(address: ShapeAddress, segment: string | number): ShapeAddress {
    return [...address, segment];
  }

  humanize(value: string): string {
    return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
  }

  fieldLabelId(fieldPath: string, address: ShapeAddress): string {
    return this.fieldDomId(fieldPath, address, 'label');
  }

  fieldHelpId(fieldPath: string, address: ShapeAddress): string {
    return this.fieldDomId(fieldPath, address, 'help');
  }

  fieldControlId(fieldPath: string, address: ShapeAddress, control: string): string {
    return this.fieldDomId(fieldPath, address, control);
  }

  private openSession(node: ConnectWorkflowNodePaletteItem, initial: ConnectGuidedModuleDraft | null): void {
    const operations = node.presentation?.operations ?? [];
    const requested = initial?.paletteId === node.paletteId
      ? operations.find(operation => operation.action === initial.action)
      : undefined;
    const defaultRef = typeof node.defaults?.ref === 'string' ? node.defaults.ref : '';
    const defaultAction = defaultRef.startsWith(`${node.paletteId}.`)
      ? defaultRef.slice(node.paletteId.length + 1)
      : '';
    const operation = requested ?? operations.find(candidate => candidate.action === defaultAction) ?? operations[0];
    this.selectedAction.set(operation?.action ?? '');
    this.fieldValues.set(operation && requested && initial
      ? this.flattenOperationInput(operation, initial.input)
      : operation ? this.requiredFixedValues(node, operation) : {});
  }

  private requiredFixedValues(
    node: ConnectWorkflowNodePaletteItem,
    operation: ConnectNodePresentationOperation,
  ): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const path of operation.requires_all ?? []) {
      const shape = node.presentation?.fields[path]?.shape;
      if (!shape || operation.effect === 'read' || shape.oneOf || shape.enum?.length !== 1) continue;
      values[path] = this.clone(shape.enum[0]);
    }
    return values;
  }

  private flattenOperationInput(
    operation: ConnectNodePresentationOperation,
    inputValue: Record<string, unknown>,
  ): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const path of operation.fields) {
      const found = this.readNestedPath(inputValue, path);
      if (found.present) values[path] = this.clone(found.value);
    }
    return values;
  }

  private buildRawInput(operation: ConnectNodePresentationOperation): Record<string, unknown> {
    const inputValue: Record<string, unknown> = {};
    for (const path of operation.fields) {
      if (!Object.hasOwn(this.fieldValues(), path)) continue;
      this.setNestedPath(inputValue, path, this.clone(this.fieldValues()[path]));
    }
    return inputValue;
  }

  private setValue(fieldPath: string, address: ShapeAddress, value: unknown): void {
    const next = this.clone(this.fieldValues()) as Record<string, unknown>;
    if (address.length === 0) {
      if (value === undefined) delete next[fieldPath];
      else next[fieldPath] = value;
      this.fieldValues.set(next);
      return;
    }
    const root = next[fieldPath] ?? (typeof address[0] === 'number' ? [] : {});
    next[fieldPath] = this.setAddress(root, address, value);
    this.fieldValues.set(next);
  }

  private setAddress(root: unknown, address: ShapeAddress, value: unknown): unknown {
    const [segment, ...rest] = address;
    if (segment === undefined) return value;
    if (typeof segment === 'number') {
      const array = Array.isArray(root) ? [...root] : [];
      array[segment] = this.setAddress(array[segment], rest, value);
      return array;
    }
    if (!this.isSafeObjectKey(segment)) return root;
    const record = this.isRecord(root) ? {...root} : {};
    if (value === undefined && rest.length === 0) delete record[segment];
    else record[segment] = this.setAddress(record[segment], rest, value);
    return record;
  }

  private defaultValue(shape: ConnectNodeFieldShape): unknown {
    if (shape.oneOf) return this.defaultValue(shape.oneOf[0]);
    if (shape.enum) return this.clone(shape.enum[0]);
    const type = this.shapeTypes(shape)[0];
    if (type === 'boolean') return false;
    if (type === 'number' || type === 'integer') return shape.minimum ?? 0;
    if (type === 'null') return null;
    if (type === 'array') return [];
    if (type === 'object') {
      const value: Record<string, unknown> = {};
      for (const required of shape.required ?? []) {
        const childShape = shape.properties?.[required];
        if (childShape) value[required] = this.defaultValue(childShape);
      }
      return value;
    }
    return '';
  }

  private shapeTypes(shape: ConnectNodeFieldShape): ConnectNodeJsonType[] {
    if (typeof shape.type === 'string') return [shape.type];
    return Array.isArray(shape.type) ? shape.type : [];
  }

  private valueMatchesShapeKind(value: unknown, shape: ConnectNodeFieldShape): boolean {
    return this.shapeTypes(shape).some(type => this.valueMatchesType(value, type));
  }

  private valueMatchesType(value: unknown, type: ConnectNodeJsonType): boolean {
    if (type === 'array') return Array.isArray(value);
    if (type === 'object') return this.isRecord(value);
    if (type === 'null') return value === null;
    if (type === 'integer') return typeof value === 'number' && Number.isInteger(value);
    if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
    return typeof value === type;
  }

  private recordValue(fieldPath: string, address: ShapeAddress): Record<string, unknown> {
    const value = this.valueAt(fieldPath, address);
    return this.isRecord(value) ? {...value} : {};
  }

  private readNestedPath(value: Record<string, unknown>, path: string): {present: boolean; value?: unknown} {
    let cursor: unknown = value;
    for (const segment of path.split('.')) {
      if (!this.isRecord(cursor) || !Object.hasOwn(cursor, segment)) return {present: false};
      cursor = cursor[segment];
    }
    return {present: true, value: cursor};
  }

  private setNestedPath(target: Record<string, unknown>, path: string, value: unknown): void {
    const segments = path.split('.');
    if (segments.some(segment => !this.isSafeObjectKey(segment))) return;
    let cursor = target;
    segments.forEach((segment, index) => {
      if (index === segments.length - 1) {
        cursor[segment] = value;
        return;
      }
      const next = Object.hasOwn(cursor, segment) && this.isRecord(cursor[segment])
        ? cursor[segment]
        : Object.create(null) as Record<string, unknown>;
      cursor[segment] = next;
      cursor = next;
    });
  }

  private clone(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(child => this.clone(child));
    if (this.isRecord(value)) {
      return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, this.clone(child)]));
    }
    return value;
  }

  private containsCurrentItemBinding(value: unknown): boolean {
    if (isConnectGuidedCurrentItemBindingToken(value)) return true;
    if (Array.isArray(value)) return value.some(child => this.containsCurrentItemBinding(child));
    return this.isRecord(value) && Object.values(value).some(child => this.containsCurrentItemBinding(child));
  }


  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private isSafeObjectKey(value: string): boolean {
    return !RESERVED_OBJECT_KEYS.has(value.toLowerCase());
  }

  private fieldDomId(fieldPath: string, address: ShapeAddress, suffix: string): string {
    const raw = [this.instanceId, fieldPath, ...address.map(String), suffix].join('-');
    return raw.replace(/[^a-zA-Z0-9_-]+/g, '-');
  }
}
